import { z } from 'zod';

/**
 * Universelle Suche (ADR 0069, erweitert um ADR 0090). Suchbare Ressourcentypen —
 * die Reihenfolge bestimmt zugleich die Reihenfolge der Ergebnisgruppen in der UI.
 *
 * Sortiert nach fachlicher Nachbarschaft (Stammdaten → Verkauf → Beschaffung →
 * Logistik → Produktion/Montage → Qualität → Finanzen → Personen → Ablage), damit
 * die Command-Palette nicht wie eine Tabellenliste wirkt. Neue Typen gehören in
 * ihren Block, nicht ans Ende.
 */
export const SEARCH_RESOURCE_TYPES = [
  // Stammdaten
  'project',
  'customer',
  'contact',
  'delivery-address',
  'article',
  'commodity-group',
  'material-code',
  'drawing-number',
  // Verkauf
  'sales-document',
  'sales-pipeline',
  // Beschaffung & Material
  'purchase-order',
  'goods-receipt',
  'forecast',
  'material-list',
  // Logistik
  'picking-order',
  'transport',
  'blocked-stock',
  'inventory-count',
  // Produktion & Montage
  'production-order',
  'assembly-task',
  'assembly-report',
  'maintenance',
  // Qualität
  'qm-process',
  // Finanzen
  'payment',
  // Personen & Organisation
  'employee',
  'position',
  'user',
  'legal-entity',
  // Ablage & Rückmeldungen
  'dms-document',
  'feedback',
] as const;

export type SearchResourceType = (typeof SEARCH_RESOURCE_TYPES)[number];

/** Deutsche Gruppen-Labels je Ressourcentyp (Anzeige in der Command-Palette). */
export const SEARCH_RESOURCE_LABELS: Record<SearchResourceType, string> = {
  project: 'Projekte',
  customer: 'Kunden',
  contact: 'Ansprechpartner',
  'delivery-address': 'Lieferadressen',
  article: 'Artikel',
  'commodity-group': 'Warengruppen',
  'material-code': 'Materialcodes',
  'drawing-number': 'Zeichnungsnummern',
  'sales-document': 'Verkaufsbelege',
  'sales-pipeline': 'Vertriebspipeline',
  'purchase-order': 'Bestellbelege',
  'goods-receipt': 'Wareneingänge',
  forecast: 'Bedarfsvorschauen',
  'material-list': 'Bedarfslisten',
  'picking-order': 'Kommissionieraufträge',
  transport: 'Transporte',
  'blocked-stock': 'Sperrlager',
  'inventory-count': 'Inventuren',
  'production-order': 'Fertigungsaufträge',
  'assembly-task': 'Montageaufträge',
  'assembly-report': 'Baustellenberichte',
  maintenance: 'Wartung',
  'qm-process': 'QM-Prozesse',
  payment: 'Zahlungen',
  employee: 'Mitarbeiter',
  position: 'Stellen',
  user: 'Benutzer',
  'legal-entity': 'Gesellschaften',
  'dms-document': 'Dateien',
  feedback: 'Verbesserungen',
};

/** Such-Query: `?q=<begriff>&limit=<treffer pro typ>`. */
export const searchQuerySchema = z.object({
  q: z.string().trim().min(1).max(120),
  limit: z.coerce.number().int().min(1).max(20).default(8),
});

export type SearchQuery = z.infer<typeof searchQuerySchema>;

/**
 * Wie ein Treffer zustande kam (ADR 0090). Der Client kann damit „ähnliche
 * Treffer" sichtbar von echten Treffern trennen — ein Ähnlichkeits-Treffer ist
 * ein Vorschlag, kein Fund.
 *
 * - `identifier` — trennzeichenfreie Kennungs-Suche („P1001" → „P-1001").
 * - `contains`   — normale Teilstring-Suche über die Textspalten (inkl. Treffer,
 *                  die über eine Belegposition auf ihren Beleg abgebildet wurden).
 * - `similar`    — Ähnlichkeitssuche über `pg_trgm` (Tippfehler-Toleranz).
 */
export const SEARCH_MATCH_KINDS = ['identifier', 'contains', 'similar'] as const;

export type SearchMatchKind = (typeof SEARCH_MATCH_KINDS)[number];

/** Ein einzelner Treffer. `type` + `id` reichen dem Frontend für die Detailroute. */
export const searchHitSchema = z.object({
  id: z.string(),
  type: z.enum(SEARCH_RESOURCE_TYPES),
  title: z.string(),
  subtitle: z.string().nullable(),
  badge: z.string().nullable(),
  /**
   * Fundstellen-Auszug aus dem durchsuchten Text (Volltext-Treffer), gekürzt und
   * mit Treffer-Markern. `null`, wenn kein sinnvoller Auszug vorliegt. Marker:
   * U+E000 (Anfang) / U+E001 (Ende) — der Client rendert sie hervorgehoben.
   */
  snippet: z.string().nullable().optional(),
  /** Wie der Treffer zustande kam (ADR 0090). */
  match: z.enum(SEARCH_MATCH_KINDS),
  /**
   * Ähnlichkeitsmaß 0..1 — **nur** bei `match: 'similar'` gesetzt, sonst `null`.
   * Höher = ähnlicher. Bewusst kein Rang für exakte Treffer: die sind keine
   * Schätzung und sollen in der UI nicht mit einer Prozentzahl relativiert werden.
   */
  score: z.number().nullable(),
});

export type SearchHit = z.infer<typeof searchHitSchema>;

/** Treffer eines Typs, gebündelt. */
export const searchGroupSchema = z.object({
  type: z.enum(SEARCH_RESOURCE_TYPES),
  label: z.string(),
  hits: z.array(searchHitSchema),
});

export type SearchGroup = z.infer<typeof searchGroupSchema>;

/**
 * Gesamtergebnis. `backend` macht transparent, welche Engine geantwortet hat —
 * seit ADR 0069 gibt es nur noch `database` (Meilisearch ist ausgebaut). Das Feld
 * bleibt, damit ein späteres zweites Backend keine API-Änderung braucht.
 */
export const searchResultSchema = z.object({
  query: z.string(),
  backend: z.enum(['database']),
  groups: z.array(searchGroupSchema),
});

export type SearchResult = z.infer<typeof searchResultSchema>;
